// Pick + resize + upload garment photos to the `garment-photos` bucket.
// All files live under `{userId}/{scanSessionId}/{i}-{ts}.jpg` so RLS works
// (insert/update/delete are gated by the first folder being the user's id).

import { supabase } from "@/integrations/supabase/client";
import { isNative } from "@/lib/capacitor";
import heic2any from "heic2any";
// pdfjs ships its worker as a separate file; Vite resolves the URL at build.
import * as pdfjsLib from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const BUCKET = "garment-photos";
const MAX_DIM = 1600;            // upload at slightly higher res than the AI thumb
const JPEG_QUALITY = 0.85;
export const MAX_PHOTOS = 5;

// ── Format detection ─────────────────────────────────────────────────────
// File.type is unreliable on iOS Safari for HEIC, and PDFs picked from the
// Files app sometimes report empty types — fall back to extension sniffing.
const extOf = (f: File) =>
  (f.name.match(/\.([a-z0-9]+)$/i)?.[1] ?? "").toLowerCase();
const isHeic = (f: File) =>
  /heic|heif/i.test(f.type) || ["heic", "heif"].includes(extOf(f));
const isPdf = (f: File) =>
  f.type === "application/pdf" || extOf(f) === "pdf";

/**
 * If the picked file is HEIC/HEIF, decode it in the browser and re-encode as
 * JPEG so every downstream step (resize, preview, upload, AI) can use a plain
 * <img>. Non-HEIC files are returned untouched.
 */
export async function normalizeHeic(file: File): Promise<File> {
  if (!isHeic(file)) return file;
  if (isNative) {
    // @capacitor/camera returns JPEG; HEIC paths come from the web file picker only.
    return file;
  }
  const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
  const blob = Array.isArray(converted) ? converted[0] : converted;
  const baseName = file.name.replace(/\.[^.]+$/, "") || "photo";
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
}

/**
 * Render every page of a PDF to a JPEG File so each page becomes a scannable
 * "photo". Used for tag scans, receipts, lookbooks, etc. Throws if the PDF
 * can't be decoded.
 */
export async function pdfToImageFiles(file: File, maxPages = MAX_PHOTOS): Promise<File[]> {
  const buf = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  const pageCount = Math.min(pdf.numPages, maxPages);
  const out: File[] = [];
  const baseName = file.name.replace(/\.[^.]+$/, "") || "pdf";
  for (let i = 1; i <= pageCount; i++) {
    const page = await pdf.getPage(i);
    // Render at ~1.8x device pixels for legibility on tag text.
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d")!;
    // PDF.js v4 expects the canvas in the render params object.
    await page.render({ canvasContext: ctx, viewport, canvas } as Parameters<typeof page.render>[0]).promise;
    const blob: Blob = await new Promise((res) =>
      canvas.toBlob((b) => res(b!), "image/jpeg", 0.9),
    );
    out.push(new File([blob], `${baseName}-p${i}.jpg`, { type: "image/jpeg" }));
  }
  return out;
}

/**
 * Take whatever the file menu handed us (camera shot, gallery image,
 * HEIC, or PDF) and return a flat list of JPEG `File`s ready for
 * `makeLocalPhoto`. Each input may produce 0–N outputs (PDFs expand;
 * unreadable files are skipped with a console warning).
 */
export async function expandPickedFiles(files: File[]): Promise<File[]> {
  const out: File[] = [];
  for (const f of files) {
    try {
      if (isPdf(f)) {
        const pages = await pdfToImageFiles(f);
        out.push(...pages);
        continue;
      }
      out.push(await normalizeHeic(f));
    } catch (err) {
      console.warn("Skipping unreadable file", f.name, err);
    }
  }
  return out;
}

export interface LocalPhoto {
  id: string;       // local-only id for React keys + remove
  file: File;       // resized blob wrapped as File for upload
  previewUrl: string; // object URL for instant preview
}

let counter = 0;
const nextId = () => `${Date.now()}-${counter++}`;

/**
 * Resize a user-picked image to a JPEG <= MAX_DIM on the longest edge.
 * Returns a fresh File so we can both preview and upload it.
 */
export async function resizeImage(file: File): Promise<File> {
  const dataUrl = await new Promise<string>((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = () => rej(r.error);
    r.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = () => rej(new Error("Could not decode image"));
    i.src = dataUrl;
  });
  const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, w, h);
  const blob: Blob = await new Promise((res) =>
    canvas.toBlob((b) => res(b!), "image/jpeg", JPEG_QUALITY)
  );
  // Strip any weird filename chars and force .jpg
  const baseName = file.name.replace(/\.[^.]+$/, "").replace(/[^a-z0-9_-]+/gi, "-").slice(0, 40) || "photo";
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
}

export async function makeLocalPhoto(file: File): Promise<LocalPhoto> {
  const resized = await resizeImage(file);
  return {
    id: nextId(),
    file: resized,
    previewUrl: URL.createObjectURL(resized),
  };
}

export function disposeLocalPhoto(p: LocalPhoto) {
  try { URL.revokeObjectURL(p.previewUrl); } catch { /* ignore */ }
}

/**
 * Upload a list of photos in parallel under {userId}/{sessionId}/.
 * Returns the public URLs in the same order as the input.
 */
export async function uploadPhotos(userId: string, sessionId: string, photos: LocalPhoto[]): Promise<string[]> {
  const uploads = photos.map(async (p, i) => {
    const path = `${userId}/${sessionId}/${i}-${Date.now()}.jpg`;
    const { error } = await supabase.storage
      .from(BUCKET)
      .upload(path, p.file, {
        contentType: "image/jpeg",
        cacheControl: "3600",
        upsert: false,
      });
    if (error) throw new Error(error.message);
    const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
    return data.publicUrl;
  });
  return Promise.all(uploads);
}

/**
 * Convert the first photo to a downscaled data URL so the AI edge function
 * (which expects an `image` data URL) doesn't have to fetch from storage.
 */
export async function fileToAnalyzeDataUrl(file: File, maxDim = 1280): Promise<string> {
  const dataUrl = await new Promise<string>((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = () => rej(r.error);
    r.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = () => rej(new Error("Could not decode image"));
    i.src = dataUrl;
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", 0.82);
}
